import React, { useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, Plus, Save, Trash2 } from "lucide-react";
import "../styles/client-portal-settings.css";
import { fetchLocations } from "../services/locations";
import {
    fetchClientPortalSettingsForOperator,
    saveClientPortalSettingsForOperator,
} from "../services/client_portal";
import ImageSquareCropper from "../components/ImageSquareCropper";
import {
    deleteOperatorNetworkLogo,
    fetchOperatorNetworkLogo,
    fetchOperatorNetworkLogoBlob,
    uploadOperatorNetworkLogo,
} from "../services/networkLogo";

const DEFAULT_FORM = {
    portal_enabled: false,
    pages: [{ id: null, page_title: "Інформація", page_url: "" }],
    show_news: true,
    show_operator_info: true,
    show_chat: true,
    show_cars: true,
    show_requests: true,
    show_polls: true,
    show_door_access: true,
    show_camera_access: true,
    show_feedbacks: true,
};

const FEATURE_FIELDS = [
    { key: "show_news", label: "Новини", networkKey: "news" },
    { key: "show_operator_info", label: "Інформація від оператора", networkKey: "operator_info" },
    { key: "show_chat", label: "Переписки", networkKey: "chat" },
    { key: "show_cars", label: "Авто", networkKey: "cars" },
    { key: "show_requests", label: "Заявки", networkKey: "requests" },
    { key: "show_polls", label: "Опитування", networkKey: "polls" },
    { key: "show_door_access", label: "Відкривання дверей", networkKey: "door_access" },
    { key: "show_camera_access", label: "Камери", networkKey: "camera_access" },
    { key: "show_feedbacks", label: "Відгуки", networkKey: "feedbacks" },
];

function formatLocationAddress(location) {
    if (!location) return "";
    const street = (location.street || "").trim();
    const house = (location.house_number || "").trim();
    const city = (location.city || "").trim();
    const streetHouse = [street, house].filter(Boolean).join(", ");
    if (city && streetHouse) return `${city}, ${streetHouse}`;
    return streetHouse || city;
}

function formatLocationLabel(location) {
    if (!location) return "Локація";
    const name = (location.location_name || "").trim();
    const address = formatLocationAddress(location);
    // Address first so same building entrances group together in the list.
    if (name && address) return `${address} — ${name}`;
    if (address) return address;
    if (name) return name;
    return `Локація ${location.id}`;
}

function applyCeilingToForm(formValues, ceiling) {
    if (!ceiling) return formValues;
    const next = { ...formValues };
    FEATURE_FIELDS.forEach((feature) => {
        if (ceiling[feature.key] === false) {
            next[feature.key] = false;
        }
    });
    return next;
}

const ClientPortalSettings = ({ networkFeatures = null }) => {
    const [scope, setScope] = useState("network"); // 'network' | 'location'
    const [locations, setLocations] = useState([]);
    const [locationId, setLocationId] = useState("");

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [form, setForm] = useState(DEFAULT_FORM);
    // Network-scope flags act as ceiling when editing a location.
    const [networkCeiling, setNetworkCeiling] = useState(null);

    const [logoUrl, setLogoUrl] = useState(null);
    const [logoPreview, setLogoPreview] = useState("");
    const [logoBusy, setLogoBusy] = useState(false);
    const [cropFile, setCropFile] = useState(null);
    const logoInputRef = useRef(null);

    const sortedLocations = useMemo(() => {
        const list = Array.isArray(locations) ? [...locations] : [];
        list.sort((a, b) => formatLocationLabel(a).localeCompare(formatLocationLabel(b), "uk"));
        return list;
    }, [locations]);

    const selectedLocation = useMemo(
        () => sortedLocations.find((item) => String(item.id) === String(locationId)) || null,
        [sortedLocations, locationId]
    );

    const productVisibleFields = useMemo(() => (
        FEATURE_FIELDS.filter((feature) => {
            if (!networkFeatures || typeof networkFeatures !== "object") return true;
            if (!feature.networkKey) return true;
            return networkFeatures[feature.networkKey] !== false;
        })
    ), [networkFeatures]);

    const visibleFeatureFields = useMemo(() => {
        // In location mode, still show fields locked by network ceiling (disabled),
        // so operator understands why they cannot turn them on.
        return productVisibleFields;
    }, [productVisibleFields]);

    const isFeatureLockedByNetwork = (featureKey) => (
        scope === "location" && networkCeiling && networkCeiling[featureKey] === false
    );

    // Keep form flags in sync with product master-switches (superadmin modules).
    useEffect(() => {
        if (!networkFeatures || typeof networkFeatures !== "object") return;
        setForm((prev) => {
            let changed = false;
            const next = { ...prev };
            FEATURE_FIELDS.forEach((feature) => {
                if (!feature.networkKey) return;
                if (networkFeatures[feature.networkKey] === false && next[feature.key]) {
                    next[feature.key] = false;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [networkFeatures]);

    const effectiveLocationId = useMemo(() => {
        if (scope !== "location") return null;
        const id = parseInt(locationId, 10);
        return Number.isFinite(id) ? id : null;
    }, [scope, locationId]);

    useEffect(() => {
        const loadLocations = async () => {
            try {
                const list = await fetchLocations();
                const active = Array.isArray(list) ? list.filter((item) => item.status !== false) : [];
                setLocations(active);
                if (!locationId && active.length > 0) {
                    setLocationId(String(active[0].id));
                }
            } catch (_) {
                setLocations([]);
            }
        };
        loadLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = "";
        (async () => {
            try {
                const data = await fetchOperatorNetworkLogo();
                if (cancelled) return;
                setLogoUrl(data?.logo_url || null);
                if (data?.logo_url) {
                    const blob = await fetchOperatorNetworkLogoBlob(data.logo_url);
                    if (cancelled) return;
                    objectUrl = URL.createObjectURL(blob);
                    setLogoPreview(objectUrl);
                } else {
                    setLogoPreview("");
                }
            } catch (_) {
                if (!cancelled) {
                    setLogoUrl(null);
                    setLogoPreview("");
                }
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, []);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setMessage("");
            try {
                // Always load network settings: used as ceiling in location mode.
                const networkSettings = await fetchClientPortalSettingsForOperator(null);
                const ceiling = {
                    show_news: networkSettings.show_news !== false,
                    show_operator_info: networkSettings.show_operator_info !== false,
                    show_chat: networkSettings.show_chat !== false,
                    show_cars: networkSettings.show_cars !== false,
                    show_requests: networkSettings.show_requests !== false,
                    show_polls: networkSettings.show_polls !== false,
                    show_door_access: networkSettings.show_door_access !== false,
                    show_camera_access: networkSettings.show_camera_access !== false,
                    show_feedbacks: networkSettings.show_feedbacks !== false,
                };
                setNetworkCeiling(ceiling);

                const s = scope === "location" && effectiveLocationId
                    ? await fetchClientPortalSettingsForOperator(effectiveLocationId)
                    : networkSettings;

                const pagesFromApi = Array.isArray(s.pages) ? s.pages : [];
                const legacyUrl = (s.portal_url || "").trim();
                const legacyTitle = (s.portal_title || "Інформація").trim() || "Інформація";

                const normalizedPages = pagesFromApi.length
                    ? pagesFromApi.map((p) => ({
                        id: p.id ?? null,
                        page_title: (p.page_title || "Інформація").trim() || "Інформація",
                        page_url: (p.page_url || "").trim(),
                    }))
                    : legacyUrl
                        ? [{ id: null, page_title: legacyTitle, page_url: legacyUrl }]
                        : [{ id: null, page_title: "Інформація", page_url: "" }];

                const nextForm = {
                    portal_enabled: !!s.portal_enabled,
                    pages: normalizedPages,
                    show_news: s.show_news !== false,
                    show_operator_info: s.show_operator_info !== false,
                    show_chat: s.show_chat !== false,
                    show_cars: s.show_cars !== false,
                    show_requests: s.show_requests !== false,
                    show_polls: s.show_polls !== false,
                    show_door_access: s.show_door_access !== false,
                    show_camera_access: s.show_camera_access !== false,
                    show_feedbacks: s.show_feedbacks !== false,
                };

                setForm(
                    scope === "location"
                        ? applyCeilingToForm(nextForm, ceiling)
                        : nextForm
                );
            } catch (e) {
                setForm(DEFAULT_FORM);
                setMessage(e.message || "Не вдалося завантажити налаштування");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [effectiveLocationId, scope]);

    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const onPickLogo = (e) => {
        const file = (e.target.files && e.target.files[0]) || null;
        e.target.value = "";
        if (!file) return;
        if (!String(file.type || "").startsWith("image/")) {
            setMessage("Оберіть файл зображення");
            return;
        }
        setCropFile(file);
        setMessage("");
    };

    const onConfirmCroppedLogo = async (croppedFile) => {
        setLogoBusy(true);
        setMessage("");
        try {
            const saved = await uploadOperatorNetworkLogo(croppedFile);
            setLogoUrl(saved?.logo_url || null);
            setCropFile(null);
            if (logoPreview) URL.revokeObjectURL(logoPreview);
            if (saved?.logo_url) {
                const blob = await fetchOperatorNetworkLogoBlob(saved.logo_url);
                setLogoPreview(URL.createObjectURL(blob));
            } else {
                setLogoPreview("");
            }
            setMessage("✅ Логотип мережі збережено");
        } catch (e) {
            setMessage(e.message || "Не вдалося зберегти логотип");
        } finally {
            setLogoBusy(false);
        }
    };

    const onDeleteLogo = async () => {
        setLogoBusy(true);
        setMessage("");
        try {
            await deleteOperatorNetworkLogo();
            setLogoUrl(null);
            if (logoPreview) URL.revokeObjectURL(logoPreview);
            setLogoPreview("");
            setMessage("✅ Логотип видалено");
        } catch (e) {
            setMessage(e.message || "Не вдалося видалити логотип");
        } finally {
            setLogoBusy(false);
        }
    };


    const updatePage = (index, patch) => {
        setForm((prev) => {
            const next = Array.isArray(prev.pages) ? [...prev.pages] : [];
            next[index] = { ...(next[index] || { id: null, page_title: "Інформація", page_url: "" }), ...patch };
            return { ...prev, pages: next };
        });
    };

    const addPage = () => {
        setForm((prev) => {
            const next = Array.isArray(prev.pages) ? [...prev.pages] : [];
            next.push({ id: null, page_title: "Інформація", page_url: "" });
            return { ...prev, pages: next };
        });
    };

    const removePage = (index) => {
        setForm((prev) => {
            const next = Array.isArray(prev.pages) ? [...prev.pages] : [];
            next.splice(index, 1);
            if (next.length === 0) next.push({ id: null, page_title: "Інформація", page_url: "" });
            return { ...prev, pages: next };
        });
    };

    const onSave = async () => {
        setLoading(true);
        setMessage("");
        try {
            if (scope === "location" && !effectiveLocationId) {
                throw new Error("Спочатку виберіть локацію");
            }

            const pagesToSend = (Array.isArray(form.pages) ? form.pages : [])
                .map((p) => ({
                    id: p.id ?? null,
                    page_title: (p.page_title || "Інформація").trim() || "Інформація",
                    page_url: (p.page_url || "").trim(),
                }))
                .filter((p) => p.page_url);

            const flags = applyCeilingToForm({
                show_news: !!form.show_news,
                show_operator_info: !!form.show_operator_info,
                show_chat: !!form.show_chat,
                show_cars: !!form.show_cars,
                show_requests: !!form.show_requests,
                show_polls: !!form.show_polls,
                show_door_access: !!form.show_door_access,
                show_camera_access: !!form.show_camera_access,
                show_feedbacks: !!form.show_feedbacks,
            }, scope === "location" ? networkCeiling : null);

            const payload = {
                location_id: effectiveLocationId,
                portal_enabled: !!form.portal_enabled,
                pages: pagesToSend,
                ...flags,
            };
            const saved = await saveClientPortalSettingsForOperator(payload);

            const savedPages = Array.isArray(saved.pages) ? saved.pages : [];
            const savedLegacyUrl = (saved.portal_url || "").trim();
            const savedLegacyTitle = (saved.portal_title || "Інформація").trim() || "Інформація";
            const normalizedPages = savedPages.length
                ? savedPages.map((p) => ({
                    id: p.id ?? null,
                    page_title: (p.page_title || "Інформація").trim() || "Інформація",
                    page_url: (p.page_url || "").trim(),
                }))
                : savedLegacyUrl
                    ? [{ id: null, page_title: savedLegacyTitle, page_url: savedLegacyUrl }]
                    : [{ id: null, page_title: "Інформація", page_url: "" }];

            const savedForm = {
                portal_enabled: !!saved.portal_enabled,
                pages: normalizedPages,
                show_news: saved.show_news !== false,
                show_operator_info: saved.show_operator_info !== false,
                show_chat: saved.show_chat !== false,
                show_cars: saved.show_cars !== false,
                show_requests: saved.show_requests !== false,
                show_polls: saved.show_polls !== false,
                show_door_access: saved.show_door_access !== false,
                show_camera_access: saved.show_camera_access !== false,
                show_feedbacks: saved.show_feedbacks !== false,
            };
            setForm(
                scope === "location"
                    ? applyCeilingToForm(savedForm, networkCeiling)
                    : savedForm
            );
            if (scope === "network") {
                setNetworkCeiling({
                    show_news: savedForm.show_news,
                    show_operator_info: savedForm.show_operator_info,
                    show_chat: savedForm.show_chat,
                    show_cars: savedForm.show_cars,
                    show_requests: savedForm.show_requests,
                    show_polls: savedForm.show_polls,
                    show_door_access: savedForm.show_door_access,
                    show_camera_access: savedForm.show_camera_access,
                    show_feedbacks: savedForm.show_feedbacks,
                });
            }
            setMessage("✅ Збережено");
        } catch (e) {
            setMessage(`❌ ${e.message || "Не вдалося зберегти"}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="client-portal-settings">
            <div className="cps-card">
                <h2>🧩 Налаштування сторінки клієнта</h2>
                <p className="cps-sub">
                    Мережа задає базову видимість модулів для всіх локацій. Локація може лише додатково
                    обмежити модулі або мати свої iframe-сторінки.
                </p>

                <div className="cps-section cps-logo-section">
                    <h3>Логотип мережі</h3>
                    <p className="cps-sub cps-section-sub">
                        Квадратний значок у шапці клієнтського додатку. Якщо не задано — клієнт бачить
                        інтерфейс як зараз.
                    </p>
                    <div className="cps-logo-row">
                        <div className={`cps-logo-preview ${logoPreview ? "" : "is-empty"}`}>
                            {logoPreview ? (
                                <img src={logoPreview} alt="Логотип мережі" />
                            ) : (
                                <span>Немає</span>
                            )}
                        </div>
                        <div className="cps-logo-actions">
                            <button
                                type="button"
                                className="add-btn"
                                disabled={logoBusy || !!cropFile}
                                onClick={() => logoInputRef.current?.click()}
                            >
                                <ImagePlus size={16} /> {logoUrl ? "Замінити" : "Завантажити"}
                            </button>
                            {logoUrl ? (
                                <button
                                    type="button"
                                    className="add-btn add-btn--secondary"
                                    disabled={logoBusy || !!cropFile}
                                    onClick={onDeleteLogo}
                                >
                                    <Trash2 size={16} /> Видалити
                                </button>
                            ) : null}
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={onPickLogo}
                            />
                        </div>
                    </div>
                    {cropFile ? (
                        <ImageSquareCropper
                            file={cropFile}
                            onCancel={() => setCropFile(null)}
                            onConfirm={onConfirmCroppedLogo}
                            confirmLabel={logoBusy ? "Збереження..." : "Зберегти квадрат"}
                        />
                    ) : null}
                </div>

                <div className="cps-row cps-scope-row">
                    <div className="cps-scope">
                        <label className="cps-radio">
                            <input
                                type="radio"
                                name="scope"
                                value="network"
                                checked={scope === "network"}
                                onChange={() => setScope("network")}
                            />
                            Для мережі
                        </label>
                        <label className="cps-radio">
                            <input
                                type="radio"
                                name="scope"
                                value="location"
                                checked={scope === "location"}
                                onChange={() => setScope("location")}
                            />
                            Для локації
                        </label>
                    </div>
                </div>

                {scope === "location" ? (
                    <div className="cps-location-picker">
                        <label className="cps-label">
                            Локація (будинок + підʼїзд)
                            <select
                                className="cps-select cps-select--wide"
                                value={locationId}
                                onChange={(e) => setLocationId(e.target.value)}
                                disabled={!sortedLocations.length}
                            >
                                {!sortedLocations.length ? (
                                    <option value="">Немає локацій</option>
                                ) : null}
                                {sortedLocations.map((l) => (
                                    <option key={l.id} value={l.id}>
                                        {formatLocationLabel(l)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {selectedLocation ? (
                            <div className="cps-location-meta">
                                Обрано: <strong>{formatLocationLabel(selectedLocation)}</strong>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <div className="cps-section">
                    <h3>Видимість модулів у клієнта</h3>
                    <p className="cps-sub cps-section-sub">
                        {scope === "network"
                            ? "Вимкнений модуль на мережі ховається у клієнтів усіх локацій."
                            : "На локації можна додатково вимкнути модуль. Увімкнути те, що вимкнено на мережі, неможливо."}
                    </p>

                    <div className="cps-features-grid">
                        {visibleFeatureFields.map((feature) => {
                            const locked = isFeatureLockedByNetwork(feature.key);
                            return (
                                <label
                                    className={`cps-checkbox cps-feature-checkbox ${locked ? "is-locked" : ""}`}
                                    key={feature.key}
                                    title={locked ? "Вимкнено на рівні мережі" : undefined}
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!form[feature.key]}
                                        disabled={locked}
                                        onChange={(e) => update({ [feature.key]: e.target.checked })}
                                    />
                                    <span>
                                        {feature.label}
                                        {locked ? <em className="cps-lock-hint"> (мережа)</em> : null}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                    {visibleFeatureFields.length === 0 ? (
                        <p className="cps-sub">Для цієї мережі суперадмін не увімкнув жодного клієнтського модуля.</p>
                    ) : null}
                </div>

                <div className="cps-section">
                    <h3>Сторінка з контентом</h3>
                    <label className="cps-checkbox">
                        <input
                            type="checkbox"
                            checked={!!form.portal_enabled}
                            onChange={(e) => update({ portal_enabled: e.target.checked })}
                        />
                        Показувати спеціальну сторінку клієнтам
                        {scope === "location" ? " (лише для обраної локації)" : " (для всієї мережі)"}
                    </label>

                    <div className="cps-pages">
                        {(Array.isArray(form.pages) ? form.pages : []).map((p, idx) => (
                            <div className="cps-page" key={p.id ?? idx}>
                                <div className="cps-page-row">
                                    <label className="cps-label cps-page-title">
                                        Назва в меню
                                        <input
                                            className="cps-input"
                                            value={p.page_title || ""}
                                            onChange={(e) => updatePage(idx, { page_title: e.target.value })}
                                            placeholder="Інформація"
                                        />
                                    </label>

                                    <button
                                        type="button"
                                        className="cps-page-remove"
                                        onClick={() => removePage(idx)}
                                        disabled={(form.pages || []).length <= 1}
                                        title="Видалити сторінку"
                                    >
                                        ✕
                                    </button>
                                </div>

                                <label className="cps-label">
                                    URL для iframe (https://...)
                                    <input
                                        className="cps-input"
                                        value={p.page_url || ""}
                                        onChange={(e) => updatePage(idx, { page_url: e.target.value })}
                                        placeholder="https://lviv.energy-ua.info/grupa/2-1"
                                    />
                                </label>
                            </div>
                        ))}

                        <button
                            type="button"
                            className="cps-page-add cps-actions-desktop"
                            onClick={addPage}
                            disabled={scope === "location" && !effectiveLocationId}
                            title={scope === "location" && !effectiveLocationId ? "Спочатку виберіть локацію" : undefined}
                        >
                            + Додати сторінку {scope === "network" ? "(для мережі)" : "(для локації)"}
                        </button>
                    </div>
                </div>

                <div className="cps-actions cps-actions-desktop">
                    <button className="cps-save" onClick={onSave} disabled={loading}>
                        {loading ? "⏳" : "💾"} Зберегти
                    </button>
                    {message && <div className="cps-message">{message}</div>}
                </div>
            </div>

            <div className="config-header cps-mobile-dock">
                <div className="header-buttons">
                    <button
                        type="button"
                        className="add-btn toolbar-icon-btn cps-dock-add"
                        onClick={addPage}
                        disabled={loading || (scope === "location" && !effectiveLocationId)}
                        title="Додати сторінку"
                        aria-label="Додати сторінку"
                    >
                        <Plus size={16} />
                    </button>
                    <button
                        type="button"
                        className="add-btn toolbar-icon-btn cps-dock-save"
                        onClick={onSave}
                        disabled={loading}
                        title="Зберегти"
                        aria-label="Зберегти"
                    >
                        <Save size={16} />
                    </button>
                </div>
            </div>
            {message ? <div className="cps-message cps-mobile-message">{message}</div> : null}
        </div>
    );
};

export default ClientPortalSettings;
